import { useEffect, useState } from 'react';
import { Box, ListItemIcon, ListItemText, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';
import { ProtectiveAdvancesGrid } from '../protectiveAdvancesGrid';
import { dateFilterProps, filterProps, noOpComparator, selectFilterProps, selectSearchFilterProps } from '@roc/ui/utils';
import { CounterPartyExpensesTab, getPayableReceivablesStatusOptions, TYPES_OF_FEE, ProtectiveAdvanceModalType, PayableReceivablesTypes } from '../../types/types';
import { usePayablesDashboardStore } from '../../hooks/usePayablesDashboardStore';
import { PayablesToolbar } from './payablesToolbar';
import { useDocumentStore } from '@roc/feature-documents';
import { CloudDownload, Check, Edit, History, MoneyOff, Tune } from '@material-ui/icons';
import { HistoryModal } from '../historyModal';
import { MarkAsPaidModal } from './markAsPaidModal';
import { MakeAnAdjustmentModal } from '../makeAnAdjustmentModal';
import { LOAN_STATUS_OPTIONS, LOAN_SUB_TYPE_OPTIONS } from '../../utils/constants';
import { ProtectiveAdvanceModal } from '../loan/protectiveAdvanceModal';
import { CreateCreditMemoModal } from './createCreditMemoModal';
import { useReceivablesDashboardStore } from '../../hooks/useReceivablesDasboardStore';

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '1400px',
  },
  paper: {
    boxShadow: 'none !important',
    border: '1px solid #cdcdcd',
    padding: '0px',
    borderRadius: '0 !important',
  },
}));

export const PayablesDashboard = observer(() => {
  const classes = useStyles();
  const { payablesApprovedStore } = usePayablesDashboardStore();
  const { receivablesDashboardStore } = useReceivablesDashboardStore();
  const { tab } = payablesApprovedStore;
  const { documentStore } = useDocumentStore();
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [payableRefNumber, setPayableRefNumber] = useState<string>('');
  const [actionSelectedRecord, setActionSelectedRecord] = useState<any>(null);
  const [openProtectiveAdvanceModal, setOpenProtectiveAdvanceModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showCreditMemoModal, setShowCreditMemoModal] = useState(false);



  useEffect(() => {
    payablesApprovedStore.fetchEntityNames();
    receivablesDashboardStore.fetchEntityNames();
  }, []);

  const openMarkAsPaidModal = () => {
    setShowMarkAsPaidModal(true);
  }

  const closeMarkAsPaidModal = () => {
    setPayableRefNumber('');
    setShowMarkAsPaidModal(false);
  };

  const handleMarkAsPaidConfirm = async () => {
    await payablesApprovedStore.markAsPaid(actionSelectedRecord?.recordId, payableRefNumber);
    payablesApprovedStore.resetToolbarInfo();
    payablesApprovedStore.setShowSelectedRecords(false);
    payablesApprovedStore.counterPartyExpenseGridStore.resetAndFetchGridData();
    closeMarkAsPaidModal();
  }

  const actions = (record, handleMenuClose) => {

    return (
      [
        <MenuItem
          disabled={!record.documentId}
          onClick={() => {
            const document = {
              taskId: record.documentId,
              originalFileName: record.originalFileName,
            };
            documentStore.downloadDocumentOriginalNameById(document);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Invoice</ListItemText>
        </MenuItem>,
        <MenuItem
          disabled={record.status !== PayableReceivablesTypes.PENDING}
          onClick={() => {
            const companyId = payablesApprovedStore.entityList.find(e => e.label === record.entity)?.value;
            setActionSelectedRecord({ ...record, companyId: companyId });
            setOpenProtectiveAdvanceModal(true);
            handleMenuClose();
            payablesApprovedStore.protectiveAdvanceFormStore.setType(ProtectiveAdvanceModalType.EDIT);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>,
        <MenuItem
          disabled={record.status === PayableReceivablesTypes.PAID}
          onClick={() => {
            setActionSelectedRecord(record);
            openMarkAsPaidModal()
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Check fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark as Paid</ListItemText>
        </MenuItem>,
        <MenuItem
          onClick={() => {
            setActionSelectedRecord(record);
            setShowCreditMemoModal(true);
            handleMenuClose();
          }}
          disabled={record.status === PayableReceivablesTypes.REJECTED ||
            record.feeType === "Credit Memo"}
        >
          <ListItemIcon>
            <MoneyOff fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Credit Memo</ListItemText>
        </MenuItem>,
        <MenuItem
          onClick={() => {
            setActionSelectedRecord(record);
            setShowAdjustmentModal(true);
            handleMenuClose();
          }}
          disabled={record.status === PayableReceivablesTypes.REJECTED ||
            record.status === PayableReceivablesTypes.PAID ||
            record.status === PayableReceivablesTypes.PROCESSING ||
            record.feeType === "Credit Memo"}
        >
          <ListItemIcon>
            <Tune fontSize="small" />
          </ListItemIcon>
          <ListItemText>Make an Adjustment</ListItemText>
        </MenuItem>,
        <MenuItem
          onClick={() => {
            payablesApprovedStore.openHistoryModal(record);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
      ]
    );
  }
  const COLUMNS: GridColumnProps[] = [
    {
      field: 'loanId',
      headerName: 'Loan Id',
      width: 90,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'loanStatus',
      headerName: 'Loan Status',
      minWidth: 150,
      comparator: noOpComparator,
      cellRenderer: 'loanStatusCellRenderer',
      ...selectFilterProps(LOAN_STATUS_OPTIONS),
    },
    {
      field: 'loanSubType',
      headerName: 'Loan Sub-type',
      minWidth: 135,
      comparator: noOpComparator,
      ...selectFilterProps(LOAN_SUB_TYPE_OPTIONS),
    },
    {
      field: 'payoffDate',
      headerName: 'Payoff Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'investor',
      headerName: 'Investor',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => receivablesDashboardStore.investorOptions)
    },
    {
      field: 'entity',
      headerName: 'Vendor',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => payablesApprovedStore.vendorOptions)
    },
    {
      field: 'invoice',
      headerName: 'Invoice #',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'transactionNumber',
      headerName: 'Transaction Id',
      minWidth: 130,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'feeType',
      headerName: 'Type',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(TYPES_OF_FEE, null),
    },
    {
      field: 'status',
      headerName: 'Payable Status',
      minWidth: 150,
      comparator: noOpComparator,
      editable: true,
      cellEditor: 'statusPayablesEditor',
      cellRenderer: 'statusCellRenderer',
      ...selectFilterProps(getPayableReceivablesStatusOptions(tab)),
    },
    {
      field: 'receivableStatus',
      headerName: 'Receivable Status',
      minWidth: 150,
      comparator: noOpComparator,
      cellRenderer: 'statusCellRenderer',
      ...selectFilterProps(getPayableReceivablesStatusOptions(CounterPartyExpensesTab.RECEIVABLE)),
    },
    {
      field: 'investorBillId',
      headerName: 'Investor Bill ID',
      minWidth: 135,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'billMonth',
      headerName: 'Investor Bill Date',
      minWidth: 145,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'investorPaidAmount',
      headerName: 'Investor Paid Amount',
      minWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'payablePaidDate',
      headerName: 'Vendor Paid Date',
      minWidth: 140,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'reimbursementDate',
      headerName: 'Investor Paid Date',
      minWidth: 145,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'originalAmount',
      headerName: 'Original Amount',
      minWidth: 135,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'adjustment',
      headerName: 'Payable Adjustment',
      minWidth: 140,
      comparator: noOpComparator,
      cellRenderer: 'linkCurrencyCellRenderer',
      ...filterProps()
    },
    {
      field: 'amount',
      headerName: 'Vendor Amount Due',
      minWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'vendorAgeing',
      headerName: 'Vendor Ageing',
      minWidth: 135,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'createdDate',
      headerName: 'Creation Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'comment',
      headerName: 'Comments',
      minWidth: 125,
      comparator: noOpComparator,
      editable: true,
      cellEditor: 'commentEditor',
      cellEditorParams: {
        type: tab,
      },
      ...filterProps()
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];

  return (
    <>
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1}>
          <Paper className={classes.paper}>
            <ProtectiveAdvancesGrid
              counterPartyExpenseStore={payablesApprovedStore}
              columns={COLUMNS}
              toolbar={<PayablesToolbar counterPartyExpenseStore={payablesApprovedStore} />}
              selectModeEnabled={true}
              actions={actions}
            />
          </Paper>
        </Box>
      </Box>
      <MarkAsPaidModal
        payableRefNumber={payableRefNumber}
        setPayableRefNumber={setPayableRefNumber}
        showMarkAsPaidModal={showMarkAsPaidModal}
        closeMarkAsPaidModal={closeMarkAsPaidModal}
        handleMarkAsPaidConfirm={handleMarkAsPaidConfirm}
      />
      <ProtectiveAdvanceModal
        record={actionSelectedRecord}
        loanId={actionSelectedRecord?.loanId ?? ''}
        open={openProtectiveAdvanceModal}
        store={payablesApprovedStore.protectiveAdvanceFormStore}
        vendors={payablesApprovedStore.entityList}
        onClose={() => setOpenProtectiveAdvanceModal(false)}
        onUpload={() => {
          payablesApprovedStore.counterPartyExpenseGridStore.resetAndFetchGridData();
          setOpenProtectiveAdvanceModal(false)
        }}
      />
      <MakeAnAdjustmentModal
        open={showAdjustmentModal}
        record={actionSelectedRecord}
        onClose={() => setShowAdjustmentModal(false)}
        onSubmit={async (amount, notes) => {
          await payablesApprovedStore.updatePayableAdjustment(actionSelectedRecord.recordId, amount, notes);
          payablesApprovedStore.counterPartyExpenseGridStore.refresh();
        }}
      />
      <CreateCreditMemoModal
        open={showCreditMemoModal}
        record={actionSelectedRecord}
        onClose={() => setShowCreditMemoModal(false)}
      />
      <HistoryModal
        open={payablesApprovedStore.historyModalOpen}
        history={payablesApprovedStore.payableHistory}
        isLoading={payablesApprovedStore.historyLoading}
        onClose={() => payablesApprovedStore.closeHistoryModal()}
        adjustmentOnly={payablesApprovedStore.historyAdjustmentOnly}
      />
    </>
  );
});
